// src/_utils/generateDebtSheet.ts
// สร้าง Excel สายเก็บเงิน format เดียวกับ 6_6_26สายA.xlsx
// ต้องติดตั้ง: npm install exceljs

import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

// ─── Types ────────────────────────────────────────────────────
export interface DebtSheetRow {
  dateStart:    Date
  days:         number
  name:         string
  amount:       number
  dailyPayment: number
  remaining:    number
  payments:     Record<string, number | string>  // key = 'D/M' → '*' | ตัวเลข | ''
  note?:        string
}

export interface DebtSheetOptions {
  zone:      string     // 'A' | 'B' | 'C'
  title?:    string
  dateRange: Date[]     // 17 วันที่จะแสดงเป็น column header
  rows:      DebtSheetRow[]
  filename?: string
}

// ─── Style helpers ────────────────────────────────────────────
const TH_DATE_FMT = '[$-1070000]d/m/yy;@'
const NUM_FMT     = '#,##0'

const thin   = { style: 'thin'   as const }
const medium = { style: 'medium' as const }

const bdrTop: Partial<ExcelJS.Borders> = {
  left: thin, right: thin, top: medium, bottom: { style: undefined as any },
}
const bdrAll: Partial<ExcelJS.Borders> = {
  left: thin, right: thin, top: thin, bottom: thin,
}

function setCell(
  row: ExcelJS.Row,
  col: number,
  value: any,
  opts: {
    fmt?:    string
    size?:   number
    bold?:   boolean
    align?:  ExcelJS.Alignment['horizontal']
    border?: Partial<ExcelJS.Borders>
    color?:  string
  } = {}
) {
  const cell = row.getCell(col)
  cell.value = value
  if (opts.fmt)    cell.numFmt = opts.fmt
  cell.font = {
    name:  'Arial',
    size:  opts.size || 8,
    bold:  opts.bold || false,
    color: opts.color ? { argb: opts.color } : undefined,
  }
  if (opts.align) {
    cell.alignment = { horizontal: opts.align, vertical: 'middle', wrapText: true }
  } else {
    cell.alignment = { vertical: 'middle' }
  }
  if (opts.border) cell.border = opts.border
}

// ═══════════════════════════════════════════════════════════════
// สรุปยอดที่ต้องเก็บ (รายวัน)
// ═══════════════════════════════════════════════════════════════
export interface CollectionRow {
  name:          string
  zone:          string
  dailyPayment:  number
  overdueDays:   number   // ค้างสะสม (วัน)
  toCollect:     number   // ยอดที่ต้องเก็บวันนี้ (รวมค้าง)
  remaining:     number   // ยอดคงเหลือทั้งหมด
  closed:        boolean  // ปิดยอดแล้ว
}

export interface CollectionSummaryOptions {
  date:      Date
  rows:      CollectionRow[]
  filename?: string
}

// สีพื้นแถวตามสถานะ
const FILL_GREEN  = 'FFC6EFCE'  // ปรกติ — เก็บตามกำหนด
const FILL_YELLOW = 'FFFFEB9C'  // ค้าง 1-2 วัน
const FILL_RED    = 'FFFFC7CE'  // ค้าง >= 3 วัน
const FILL_GRAY   = 'FFF2F2F2'  // ปิดยอดแล้ว
const FONT_GREEN  = 'FF006100'
const FONT_YELLOW = 'FF9C6500'
const FONT_RED    = 'FF9C0006'
const FONT_GRAY   = 'FF808080'

function rowStyle(r: CollectionRow): { fill: string; font: string; label: string } {
  if (r.closed)            return { fill: FILL_GRAY,   font: FONT_GRAY,   label: 'ปิดยอดแล้ว' }
  if (r.overdueDays <= 0)  return { fill: FILL_GREEN,  font: FONT_GREEN,  label: 'ปรกติ' }
  if (r.overdueDays <= 2)  return { fill: FILL_YELLOW, font: FONT_YELLOW, label: `ค้าง ${r.overdueDays} วัน` }
  return                          { fill: FILL_RED,    font: FONT_RED,    label: `ค้างสะสม ${r.overdueDays} วัน` }
}

export async function generateCollectionSummary(opts: CollectionSummaryOptions): Promise<void> {
  const { date, rows, filename } = opts
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('สรุปยอดเก็บ')

  // ── Column widths ──────────────────────────────────────────
  const widths = [6, 26, 8, 13, 12, 15, 15, 16]
  widths.forEach((w, i) => { ws.getColumn(i + 1).width = w })

  // ── Title ────────────────────────────────────────────────────
  ws.mergeCells(1, 1, 1, widths.length)
  const titleCell = ws.getCell(1, 1)
  titleCell.value = `สรุปยอดที่ต้องเก็บ — วันที่ ${date.toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
  titleCell.font = { name: 'Arial', size: 13, bold: true }
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
  ws.getRow(1).height = 24

  // ── Legend ─────────────────────────────────────────────────
  const legendRow = ws.getRow(2)
  const legends: [string, string, string][] = [
    ['ปรกติ',          FILL_GREEN,  FONT_GREEN],
    ['ค้าง 1-2 วัน',   FILL_YELLOW, FONT_YELLOW],
    ['ค้างสะสม ≥3 วัน', FILL_RED,    FONT_RED],
    ['ปิดยอดแล้ว',     FILL_GRAY,   FONT_GRAY],
  ]
  legends.forEach(([label, fill, font], i) => {
    const c = legendRow.getCell(i + 1)
    c.value = label
    c.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } }
    c.font  = { name: 'Arial', size: 8, color: { argb: font }, bold: true }
    c.alignment = { horizontal: 'center', vertical: 'middle' }
    c.border = bdrAll
  })
  legendRow.height = 16

  // ── Header row ──────────────────────────────────────────────
  const headers = ['ลำดับ', 'ชื่อลูกค้า', 'โซน', 'ยอดเก็บ/วัน', 'ค้างสะสม (วัน)', 'ยอดที่ต้องเก็บวันนี้', 'ยอดคงเหลือทั้งหมด', 'สถานะ']
  const hdrRow = ws.getRow(3)
  headers.forEach((h, i) => {
    const c = hdrRow.getCell(i + 1)
    c.value = h
    c.font  = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFFFF' } }
    c.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } }
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    c.border = bdrAll
  })
  hdrRow.height = 28

  // ── Data rows — เรียงตามโซน แล้วชื่อ ────────────────────────
  const sorted = [...rows].sort((a, b) => {
    if (a.zone !== b.zone) return a.zone.localeCompare(b.zone, 'th')
    return a.name.localeCompare(b.name, 'th')
  })

  let r = 4
  let zoneTotalDaily = 0, zoneTotalCollect = 0, zoneTotalRemain = 0
  let currentZone: string | null = null

  const writeZoneSubtotal = () => {
    if (currentZone === null) return
    const row = ws.getRow(r)
    setCell(row, 1, '', { border: bdrAll })
    setCell(row, 2, `รวมโซน ${currentZone}`, { bold: true, align: 'left', border: bdrAll })
    setCell(row, 3, '', { border: bdrAll })
    setCell(row, 4, zoneTotalDaily,   { fmt: NUM_FMT, bold: true, align: 'right', border: bdrAll })
    setCell(row, 5, '', { border: bdrAll })
    setCell(row, 6, zoneTotalCollect, { fmt: NUM_FMT, bold: true, align: 'right', border: bdrAll })
    setCell(row, 7, zoneTotalRemain,  { fmt: NUM_FMT, bold: true, align: 'right', border: bdrAll })
    setCell(row, 8, '', { border: bdrAll })
    for (let col = 1; col <= 8; col++) {
      row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAEAEA' } }
    }
    row.height = 16
    r++
  }

  sorted.forEach((row, idx) => {
    if (currentZone !== null && row.zone !== currentZone) {
      writeZoneSubtotal()
      zoneTotalDaily = 0; zoneTotalCollect = 0; zoneTotalRemain = 0
    }
    currentZone = row.zone

    const style = rowStyle(row)
    const xr = ws.getRow(r)

    setCell(xr, 1, idx + 1,            { align: 'center', border: bdrAll })
    setCell(xr, 2, row.name,           { size: 9, align: 'left', border: bdrAll })
    setCell(xr, 3, row.zone,           { align: 'center', border: bdrAll })
    setCell(xr, 4, row.dailyPayment,   { fmt: NUM_FMT, align: 'right', border: bdrAll })
    setCell(xr, 5, row.overdueDays,    { align: 'center', border: bdrAll })
    setCell(xr, 6, row.toCollect,      { fmt: NUM_FMT, align: 'right', border: bdrAll })
    setCell(xr, 7, row.remaining,      { fmt: NUM_FMT, align: 'right', border: bdrAll })
    setCell(xr, 8, style.label,        { align: 'center', border: bdrAll })

    for (let col = 1; col <= 8; col++) {
      const c = xr.getCell(col)
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.fill } }
      c.font = { name: 'Arial', size: c.font?.size || 9, bold: c.font?.bold, color: { argb: style.font } }
    }
    xr.height = 16

    zoneTotalDaily   += row.dailyPayment
    zoneTotalCollect += row.toCollect
    zoneTotalRemain  += row.remaining
    r++
  })
  writeZoneSubtotal()

  // ── Grand total ──────────────────────────────────────────────
  const grandRow = ws.getRow(r)
  setCell(grandRow, 1, '', { border: bdrAll })
  setCell(grandRow, 2, 'รวมทั้งหมด', { bold: true, align: 'left', border: bdrAll })
  setCell(grandRow, 3, '', { border: bdrAll })
  setCell(grandRow, 4, rows.reduce((s, x) => s + x.dailyPayment, 0), { fmt: NUM_FMT, bold: true, align: 'right', border: bdrAll })
  setCell(grandRow, 5, '', { border: bdrAll })
  setCell(grandRow, 6, rows.reduce((s, x) => s + x.toCollect, 0),    { fmt: NUM_FMT, bold: true, align: 'right', border: bdrAll })
  setCell(grandRow, 7, rows.reduce((s, x) => s + x.remaining, 0),    { fmt: NUM_FMT, bold: true, align: 'right', border: bdrAll })
  setCell(grandRow, 8, '', { border: bdrAll })
  for (let col = 1; col <= 8; col++) {
    grandRow.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD966' } }
  }
  grandRow.height = 18

  ws.views = [{ state: 'frozen', ySplit: 3 }]

  // ── Download ────────────────────────────────────────────────
  const buf  = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const dateStr = date.toISOString().slice(0, 10)
  saveAs(blob, filename || `สรุปยอดเก็บ_${dateStr}.xlsx`)
}

// ─── Main generator (สายเก็บเงิน) ──────────────────────────────
export async function generateDebtSheet(opts: DebtSheetOptions): Promise<void> {
  const { zone, dateRange, rows, filename } = opts
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Sheet1')

  // ── Column widths ──────────────────────────────────────────
  ws.getColumn(1).width = 2    // A
  ws.getColumn(2).width = 2    // B
  ws.getColumn(3).width = 2    // C
  ws.getColumn(4).width = 9.14 // D วันที่เริ่ม
  ws.getColumn(5).width = 5    // E จำนวนวัน
  ws.getColumn(6).width = 24.4 // F ชื่อ
  ws.getColumn(7).width = 9.14 // G ยอดกู้
  ws.getColumn(8).width = 9.14 // H ส่งวันละ
  ws.getColumn(9).width = 9.86 // I ยอดค้าง
  for (let i = 10; i <= 26; i++) {
    ws.getColumn(i).width = 9.14
  }

  // ── Row 1-4 เว้นว่าง (เหมือน original) ────────────────────
  for (let r = 1; r <= 4; r++) ws.getRow(r).height = 15

  // ── Row 5: Header ──────────────────────────────────────────
  ws.getRow(5).height = 30.75
  const hdr = ws.getRow(5)

  const hdrDefs: [number, string, ExcelJS.Alignment['horizontal']][] = [
    [4, 'วันที่เริ่ม',    'center'],
    [5, 'กำนดวันกู้',    'center'],
    [6, 'รายชื่อ',       'center'],
    [7, 'ยอดกู้ (กีบ)', 'left'],
    [8, 'ส่งวันละ',      'center'],
    [9, 'ยอดค้างเก็บ',  'center'],
  ]
  hdrDefs.forEach(([col, label, align]) => {
    setCell(hdr, col, label, { align, border: bdrAll })
  })

  // date header cols J(10) - Z(26) = 17 วัน
  dateRange.slice(0, 17).forEach((d, i) => {
    setCell(hdr, 10 + i, d, { fmt: TH_DATE_FMT, align: 'center', border: bdrAll })
  })

  // ── Data rows (2 rows per customer) ────────────────────────
  rows.forEach((r, idx) => {
    const mainRowNum = 6 + idx * 2
    const noteRowNum = mainRowNum + 1

    ws.getRow(mainRowNum).height = 15
    ws.getRow(noteRowNum).height = 15

    const mr = ws.getRow(mainRowNum)
    const nr = ws.getRow(noteRowNum)

    // Main row: D E F G H I
    setCell(mr, 4, r.dateStart,    { fmt: TH_DATE_FMT, align: 'center', border: bdrTop })
    setCell(mr, 5, r.days,         { align: 'center', border: bdrTop })
    setCell(mr, 6, r.name,         { size: 9, align: 'left', border: bdrTop })
    setCell(mr, 7, r.amount,       { fmt: NUM_FMT, align: 'right', border: bdrTop })
    setCell(mr, 8, r.dailyPayment, { fmt: NUM_FMT, align: 'right', border: bdrTop })
    setCell(mr, 9, r.remaining,    { fmt: NUM_FMT, align: 'right', border: bdrTop })

    // Payment cols J-Z
    dateRange.slice(0, 17).forEach((d, i) => {
      const col = 10 + i
      const key = `${d.getDate()}/${d.getMonth() + 1}`
      const pay = r.payments[key]

      if (typeof pay === 'number' && pay > 0) {
        setCell(mr, col, pay, { fmt: NUM_FMT, align: 'center', border: bdrTop })
      } else if (pay === '*' || pay === 'ล้มบ/ช') {
        setCell(mr, col, pay, { align: 'center', border: bdrTop })
      } else {
        setCell(mr, col, '', { border: bdrTop })
      }
    })

    // Note row: col F = note, others empty with border
    setCell(nr, 6, r.note || '', {
      size: 8, align: 'center', border: bdrAll, color: 'FF0000FF',
    })
    for (let col = 4; col <= 26; col++) {
      if (col !== 6) {
        const c = nr.getCell(col)
        c.border = bdrAll
      }
    }
  })

  // ── Download ────────────────────────────────────────────────
  const buf  = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '_')
  saveAs(blob, filename || `สาย${zone}_${today}.xlsx`)
}